
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import List from '@/common/components/UI/List/index.vue'
import ReportListItem from '@/modules/KPIModule/components/ReportListItem/index.vue'

// validation
import useVuelidate from '@vuelidate/core'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { KPIT, ReportT } from '@/core/types/kpi.types'

// composable
import { required } from '@vuelidate/validators'

// realisations
import { kpi } from '@/core/realisations/kpi.realisation'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-select': TheSelect,
        'the-button': TheButton,
        list: List,
        'report-list-item': ReportListItem,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            is_report_ready: false,

            telephony_id: null as number | null,
            delivery_id: null as number | null,

            report: null as ReportT[] | null,
        })

        const GetReportValidation = {
            telephony_id: { required },
            delivery_id: { required },
        }

        const state_form$ = useVuelidate(GetReportValidation, state)

        const data_state = reactive({
            telephony: new DataState<KPIT>({}),
            delivery: new DataState<KPIT>({}),
        })

        const loadDelivery = async (page: number) => {
            data_state.delivery.pagination.current_page = page
            const params = {
                page,
            }

            try {
                data_state.delivery.is_loading = true

                const data = await kpi.getDeliveryList(params)
                data_state.delivery.list = data.data

                data_state.delivery.pagination.total_pages = data.meta.last_page

                data_state.delivery.is_failed = false
                data_state.delivery.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.delivery.failure.catchError(error)

                if (!data_state.delivery.failure.error_state.is_cancel) {
                    data_state.delivery.is_failed = true
                }
            } finally {
                if (!data_state.delivery.failure.error_state.is_cancel) {
                    data_state.delivery.is_loading = false
                }
            }
        }

        const loadTelephony = async (page: number) => {
            data_state.telephony.pagination.current_page = page
            const params = {
                page,
            }

            try {
                data_state.telephony.is_loading = true

                const data = await kpi.getTelephonyList(params)
                data_state.telephony.list = data.data

                data_state.telephony.pagination.total_pages = data.meta.last_page

                data_state.telephony.is_failed = false
                data_state.telephony.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.telephony.failure.catchError(error)

                if (!data_state.telephony.failure.error_state.is_cancel) {
                    data_state.telephony.is_failed = true
                }
            } finally {
                if (!data_state.telephony.failure.error_state.is_cancel) {
                    data_state.telephony.is_loading = false
                }
            }
        }

        const refreshDelivery = () => {
            loadDelivery(data_state.delivery.pagination.current_page)
        }

        const refreshTelephony = () => {
            loadTelephony(data_state.telephony.pagination.current_page)
        }

        const loadMoreDelivery = async (page: number) => {
            if (page > data_state.delivery.pagination.total_pages) return
            data_state.delivery.pagination.current_page = page
            const params = {
                page,
            }

            try {
                data_state.delivery.is_loading = true

                const response = await kpi.getDeliveryList(params)
                data_state.delivery.list = data_state.delivery.list.concat(response.data)

                data_state.delivery.pagination.total_pages = response.meta.last_page

                data_state.delivery.is_failed = false
                data_state.delivery.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.delivery.failure.catchError(error)

                if (!data_state.delivery.failure.error_state.is_cancel) {
                    data_state.delivery.is_failed = true
                }
            } finally {
                if (!data_state.delivery.failure.error_state.is_cancel) {
                    data_state.delivery.is_loading = false
                }
            }
        }

        const loadMoreTelephony = async (page: number) => {
            if (page > data_state.telephony.pagination.total_pages) return
            data_state.telephony.pagination.current_page = page
            const params = {
                page,
            }

            try {
                data_state.telephony.is_loading = true

                const response = await kpi.getTelephonyList(params)
                data_state.telephony.list = data_state.telephony.list.concat(response.data)

                data_state.telephony.pagination.total_pages = response.meta.last_page

                data_state.telephony.is_failed = false
                data_state.telephony.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.telephony.failure.catchError(error)

                if (!data_state.telephony.failure.error_state.is_cancel) {
                    data_state.telephony.is_failed = true
                }
            } finally {
                if (!data_state.telephony.failure.error_state.is_cancel) {
                    data_state.telephony.is_loading = false
                }
            }
        }

        const getReport = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (!state.telephony_id || !state.delivery_id) return

            try {
                state.is_loading = true
                const form_data = new FormData()

                form_data.append('telephony_id', state.telephony_id.toString())
                form_data.append('delivery_id', state.delivery_id.toString())

                const data = await kpi.getReport(form_data)
                state.report = data.data

                state.is_report_ready = true
            } catch (error) {
                console.log(error)
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadDelivery(1)
            loadTelephony(1)
        })

        return {
            state,
            state_form$,
            data_state,
            refreshTelephony,
            loadMoreTelephony,
            refreshDelivery,
            loadMoreDelivery,
            getReport,
        }
    },
})
